import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  increment,
} from "firebase/firestore"
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { db } from "../firebase"

// Keep channel ids aligned with the UI you requested: general + profitloss.
export const COMMUNITY_CHANNELS = ["general", "profitloss"]

export const POSTS_PAGE_SIZE = 15
export const NEW_POSTS_SUBSCRIBE_LIMIT = 10
export const COMMUNITY_POST_MAX_IMAGES = 4
export const COMMENTS_SUBSCRIBE_LIMIT = 50
export const COMMENTS_PAGE_SIZE = 15
export const NEW_COMMENTS_SUBSCRIBE_LIMIT = 20

function normalizeChannel(channel) {
  if (!channel) return null
  if (channel === "profit-loss") return "profitloss"
  return channel
}

function normalizeTag(tag) {
  if (!tag) return null
  const trimmed = String(tag).trim()
  if (!trimmed) return null
  return trimmed.replace(/^#/, "").toLowerCase()
}

function docToPost(id, data) {
  const raw = data?.createdAt
  const createdAt =
    raw != null && typeof raw?.toMillis === "function" ? raw.toMillis() : null

  return {
    id,
    uid: (data?.uid ?? "").toString(),
    username: (data?.username ?? "").toString(),
    avatar: (data?.avatar ?? "").toString(),
    verified: Boolean(data?.verified ?? data?.is_verified ?? false),
    channel: normalizeChannel(data?.channel) ?? "general",
    message: (data?.message ?? "").toString(),
    tickers: Array.isArray(data?.tickers) ? data.tickers : [],
    tags: Array.isArray(data?.tags) ? data.tags : [],
    images: Array.isArray(data?.images) ? data.images : [],
    likes: Number(data?.likes ?? 0),
    replyCount: Number(data?.replyCount ?? 0),
    createdAt,
  }
}

function collPosts() {
  return collection(db, "communityPosts")
}

function buildPostsQuery(filters, pageLimit, startAfterDoc) {
  const constraints = []

  if (filters?.channel != null) {
    constraints.push(where("channel", "==", normalizeChannel(filters.channel)))
  }

  const tag = normalizeTag(filters?.tag)
  if (tag) {
    // tags are stored as lower-case strings without '#'
    constraints.push(where("tags", "array-contains", tag))
  }

  constraints.push(orderBy("createdAt", "desc"))
  constraints.push(limit(pageLimit))

  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc))
  }

  return query(collPosts(), ...constraints)
}

export async function fetchCommunityPosts(filters, pageSize = POSTS_PAGE_SIZE, startAfterDoc = null) {
  const q = buildPostsQuery(filters, pageSize, startAfterDoc)
  const snapshot = await getDocs(q)
  const posts = snapshot.docs.map((d) => docToPost(d.id, d.data()))
  const lastDoc = snapshot.docs.length === pageSize ? snapshot.docs[snapshot.docs.length - 1] : null
  return { posts, lastDoc }
}

/**
 * Subscribe to new posts only (createdAt > newerThanMs).
 * Returns unsubscribe function.
 */
export function subscribeToNewCommunityPosts(filters, newerThanMs, onNewPosts) {
  if (newerThanMs == null) return () => {}

  const newerThan = Timestamp.fromMillis(newerThanMs)
  const constraints = []

  if (filters?.channel != null) {
    constraints.push(where("channel", "==", normalizeChannel(filters.channel)))
  }

  const tag = normalizeTag(filters?.tag)
  if (tag) {
    constraints.push(where("tags", "array-contains", tag))
  }

  constraints.push(where("createdAt", ">", newerThan))
  constraints.push(orderBy("createdAt", "desc"))
  constraints.push(limit(NEW_POSTS_SUBSCRIBE_LIMIT))

  const q = query(collPosts(), ...constraints)
  return onSnapshot(q, (snapshot) => {
    const posts = snapshot.docs.map((d) => docToPost(d.id, d.data()))
    if (posts.length) onNewPosts([...posts].reverse()) // oldest-first within this batch
  })
}

export function subscribeToComments(postId, onComments) {
  const commentsColl = collection(db, "communityPosts", postId, "comments")
  // Keep this bounded to reduce read cost on popular threads.
  const q = query(
    commentsColl,
    orderBy("createdAt", "desc"),
    limit(COMMENTS_SUBSCRIBE_LIMIT)
  )
  return onSnapshot(q, (snapshot) => {
    const comments = snapshot.docs.map((d) => {
      const data = d.data()
      const raw = data?.createdAt
      const createdAt =
        raw != null && typeof raw?.toMillis === "function" ? raw.toMillis() : null
      return {
        id: d.id,
        postId,
        uid: (data?.uid ?? "").toString(),
        username: (data?.username ?? "").toString(),
        avatar: (data?.avatar ?? "").toString(),
        message: (data?.message ?? "").toString(),
        createdAt,
      }
    })
    // UI expects oldest -> newest order, so reverse the bounded list.
    onComments([...comments].reverse())
  })
}

export async function fetchCommunityComments(postId, pageSize = COMMENTS_PAGE_SIZE, startAfterDoc = null) {
  const commentsColl = collection(db, "communityPosts", postId, "comments")
  const constraints = [orderBy("createdAt", "desc"), limit(pageSize)]
  if (startAfterDoc) constraints.push(startAfter(startAfterDoc))
  const q = query(commentsColl, ...constraints)
  const snapshot = await getDocs(q)
  const comments = snapshot.docs.map((d) => {
    const data = d.data()
    const raw = data?.createdAt
    const createdAt =
      raw != null && typeof raw?.toMillis === "function" ? raw.toMillis() : null
    return {
      id: d.id,
      postId,
      uid: (data?.uid ?? "").toString(),
      username: (data?.username ?? "").toString(),
      avatar: (data?.avatar ?? "").toString(),
      message: (data?.message ?? "").toString(),
      createdAt,
    }
  })
  const lastDoc = snapshot.docs.length === pageSize ? snapshot.docs[snapshot.docs.length - 1] : null
  return { comments: [...comments].reverse(), lastDoc }
}

export function subscribeToNewComments(postId, newerThanMs, onNewComments) {
  if (newerThanMs == null) return () => {}
  const commentsColl = collection(db, "communityPosts", postId, "comments")
  const newerThan = Timestamp.fromMillis(newerThanMs)
  const q = query(
    commentsColl,
    where("createdAt", ">", newerThan),
    orderBy("createdAt", "asc"),
    limit(NEW_COMMENTS_SUBSCRIBE_LIMIT)
  )
  return onSnapshot(q, (snapshot) => {
    const comments = snapshot.docs.map((d) => {
      const data = d.data()
      const raw = data?.createdAt
      const createdAt =
        raw != null && typeof raw?.toMillis === "function" ? raw.toMillis() : null
      return {
        id: d.id,
        postId,
        uid: (data?.uid ?? "").toString(),
        username: (data?.username ?? "").toString(),
        avatar: (data?.avatar ?? "").toString(),
        message: (data?.message ?? "").toString(),
        createdAt,
      }
    })
    if (comments.length) onNewComments(comments)
  })
}

export async function uploadCommunityImage(userId, file) {
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase()
  const path = `community-posts/${userId}/${Date.now()}_${Math.random().toString(36).slice(2, 9)}.${ext}`
  const storage = getStorage()
  const storageRef = ref(storage, path)
  await uploadBytes(storageRef, file)
  return getDownloadURL(storageRef)
}

export async function getUserVerified(uid) {
  if (!uid) return false
  const userRef = doc(db, "users", uid)
  const snap = await getDoc(userRef)
  const data = snap.data()
  return Boolean(data?.is_verified ?? data?.verified ?? false)
}

export async function createCommunityPost(input) {
  const docRef = await addDoc(collPosts(), {
    ...input,
    verified: Boolean(input.verified),
    tickers: [], // intentionally omitted from UI + filtering
    likes: 0,
    replyCount: 0,
    images: input.images ?? [],
    createdAt: serverTimestamp(),
  })
  // Avoid a follow-up read after write. Realtime listener will reconcile canonical values.
  return {
    id: docRef.id,
    uid: input.uid,
    username: input.username,
    avatar: input.avatar ?? "",
    verified: Boolean(input.verified),
    channel: normalizeChannel(input.channel) ?? "general",
    message: input.message ?? "",
    tickers: [],
    tags: Array.isArray(input.tags) ? input.tags : [],
    images: Array.isArray(input.images) ? input.images : [],
    likes: 0,
    replyCount: 0,
    createdAt: Date.now(),
  }
}

export async function likeCommunityPost(postId) {
  const postRef = doc(db, "communityPosts", postId)
  await updateDoc(postRef, {
    likes: increment(1),
  })
}

export async function addCommunityComment(postId, input) {
  const postRef = doc(db, "communityPosts", postId)
  await updateDoc(postRef, { replyCount: increment(1) })

  const commentRef = await addDoc(collection(db, "communityPosts", postId, "comments"), {
    ...input,
    createdAt: serverTimestamp(),
  })
  // Avoid a follow-up read here too.
  return {
    id: commentRef.id,
    postId,
    uid: (input?.uid ?? "").toString(),
    username: (input?.username ?? "").toString(),
    avatar: (input?.avatar ?? "").toString(),
    message: (input?.message ?? "").toString(),
    createdAt: Date.now(),
  }
}

